import { useState, useCallback } from "react";
import { fetchKpiUnitsAsync, fetchKpiDataTypesAsync } from "@features/admin/kpi/services/kpiSvc";
import { KpiUnit } from "@features/admin/kpi/types/kpiUnits";
import { KpiDataType } from "@features/admin/kpi/types/kpiDataTypes";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { SORT_BY_UNIT_NAME, SORT_DIR_ASC } from "@core/constants/listSort";
import { useScopeId } from "@core/hooks/useRouteParamId";

/**
 * Custom React hook that provides KPI options and related loading/error states.
 *
 * @remarks
 * - Maintains two separate sets of state for KPI Units and KPI Data Types:
 *   - the items array, a loading boolean, and an error message (string | null) for each.
 * - Exposes two async fetch functions to load each resource independently:
 *   - fetchKpiUnits() and fetchKpiDataTypes().
 * - Each fetch function sets loading/error state, calls the corresponding async API helper
 *   (e.g. fetchKpiUnitsAsync / fetchKpiDataTypesAsync), writes the returned data into state,
 *   and returns a cleanup callback that sets an internal `cancelled` flag to avoid updating
 *   state after the caller has unmounted or cancelled the operation.
 *
 * @returns An object with the following properties:
 * - `kpiUnits: Array<KpiUnit>` — currently loaded KPI units (defaults to []).
 * - `kpiUnitsLoading: boolean` — true while KPI units are being fetched.
 * - `kpiUnitsError: string | null` — error message if KPI units fetch fails.
 * - `kpiDataTypes: Array<KpiDataType>` — currently loaded KPI data types (defaults to []).
 * - `kpiDataTypesLoading: boolean` — true while KPI data types are being fetched.
 * - `kpiDataTypesError: string | null` — error message if KPI data types fetch fails.
 * - `fetchKpiUnits: () => Promise<() => void>` — async function to fetch KPI units. Resolves to a cleanup function that, when called, prevents subsequent state updates from that fetch.
 * - `fetchKpiDataTypes: () => Promise<() => void>` — async function to fetch KPI data types. Resolves to a cleanup function that, when called, prevents subsequent state updates from that fetch.
 *
 */
export function useKpiOptions() {
    // KPI Units
    const [kpiUnits, setKpiUnits] = useState<Array<KpiUnit>>([]);
    const [kpiUnitsLoading, setKpiUnitsLoading] = useState(false);
    const [kpiUnitsError, setKpiUnitsError] = useState<string | null>(null);

    // KPI Data Types
    const [kpiDataTypes, setKpiDataTypes] = useState<Array<KpiDataType>>([]);
    const [kpiDataTypesLoading, setKpiDataTypesLoading] = useState(false);
    const [kpiDataTypesError, setKpiDataTypesError] = useState<string | null>(null);

    const resolvedNationId = useScopeId() ?? 0;

    // Fetch KPI Units
    const fetchKpiUnits = useCallback(async () => {
        let cancelled = false;

        // Reset state
        setKpiUnitsLoading(true);
        setKpiUnitsError(null);

        try {
            const allItems = await fetchAllPages<KpiUnit>((page) =>
                fetchKpiUnitsAsync(resolvedNationId, {
                    all: true,
                    page,
                    sortBy: SORT_BY_UNIT_NAME,
                    sortDir: SORT_DIR_ASC,
                })
            );

            if (!cancelled) {
                setKpiUnits(allItems);
            }
        } catch (e: any) {
            if (!cancelled) {
                setKpiUnitsError(e?.message ?? "Failed to load KPI units.");
            }
        } finally {
            if (!cancelled) {
                setKpiUnitsLoading(false);
            }
        }

        return () => { cancelled = true; };

    }, [resolvedNationId]);

    // Fetch KPI Data Types
    const fetchKpiDataTypes = useCallback(async () => {
        let cancelled = false;

        // Reset state
        setKpiDataTypesLoading(true);
        setKpiDataTypesError(null);

        try {
            const allItems = await fetchAllPages<KpiDataType>((page) =>
                fetchKpiDataTypesAsync(resolvedNationId, { all: true, page })
            );

            if (!cancelled) {
                setKpiDataTypes(allItems);
            }
        } catch (e: any) {
            if (!cancelled) {
                setKpiDataTypesError(e?.message ?? "Failed to load KPI data types.");
            }
        } finally {
            if (!cancelled) {
                setKpiDataTypesLoading(false);
            }
        }

        return () => { cancelled = true; };

    }, [resolvedNationId]);

    // Return state and fetch functions
    return { kpiUnits, kpiUnitsLoading, kpiUnitsError, kpiDataTypes, kpiDataTypesLoading, kpiDataTypesError, fetchKpiUnits, fetchKpiDataTypes };
}